import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface MobileBottomNavProps {
  onOpenScanner: () => void;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onOpenScanner }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) return null;

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const isAdmin = ['super_admin', 'master_admin'].includes(user?.role || '');
  const isTechnician = ['technician', 'field_staff'].includes(user?.role || '');

  return (
    <nav
      aria-label="Mobile Navigation"
      className="no-print fixed bottom-0 left-0 right-0 z-40 md:hidden mobile-bottom-bar shadow-[0_-8px_30px_rgba(0,0,0,0.5)] border-t border-white/[0.08]"
    >
      <div className="grid grid-cols-5 items-center h-16 max-w-lg mx-auto px-2">
        {/* Tab 1: Dashboard */}
        <Link
          to="/dashboard"
          className={`flex flex-col items-center justify-center py-1 transition-all ${
            isActive('/dashboard') ? 'text-orange-400' : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <div className="relative">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={isActive('/dashboard') ? 2.5 : 1.8}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            {isActive('/dashboard') && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-orange-500"></span>
            )}
          </div>
          <span className="text-[10px] font-bold mt-1 tracking-tight">Home</span>
        </Link>

        {/* Tab 2: Hardware Registry */}
        <Link
          to="/items"
          className={`flex flex-col items-center justify-center py-1 transition-all ${
            isActive('/items') && !isActive('/items/search')
              ? 'text-orange-400'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <div className="relative">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={isActive('/items') ? 2.5 : 1.8}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            {isActive('/items') && !isActive('/items/search') && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-orange-500"></span>
            )}
          </div>
          <span className="text-[10px] font-bold mt-1 tracking-tight">Registry</span>
        </Link>

        {/* Tab 3 (Center): Quick Scan Elevated Trigger */}
        <div className="flex flex-col items-center justify-center relative -top-3">
          <button
            type="button"
            onClick={onOpenScanner}
            className="w-13 h-13 rounded-full bg-gradient-to-tr from-red-600 via-orange-500 to-amber-500 p-0.5 shadow-lg shadow-orange-500/40 active:scale-90 transition-transform flex items-center justify-center border-2 border-[#0a0e17]"
            title="Scan QR Code"
            aria-label="Scan QR Code"
          >
            <div className="w-full h-full rounded-full flex items-center justify-center bg-gradient-to-tr from-red-600 via-orange-500 to-amber-500 text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.2}
                  d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                />
              </svg>
            </div>
          </button>
          <span className="text-[9px] font-extrabold text-orange-400 mt-0.5 tracking-tight uppercase">
            Scan
          </span>
        </div>

        {/* Tab 4: Blank QRs for Admin, or Technicians for non-admin */}
        {isAdmin ? (
          <Link
            to="/admin/blank-qr"
            className={`flex flex-col items-center justify-center py-1 transition-all ${
              isActive('/admin/blank-qr') ? 'text-orange-400' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <div className="relative">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={isActive('/admin/blank-qr') ? 2.5 : 1.8}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              {isActive('/admin/blank-qr') && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-orange-500"></span>
              )}
            </div>
            <span className="text-[10px] font-bold mt-1 tracking-tight">Blank QRs</span>
          </Link>
        ) : (
          <Link
            to="/technicians"
            className={`flex flex-col items-center justify-center py-1 transition-all ${
              isActive('/technicians') ? 'text-orange-400' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <div className="relative">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={isActive('/technicians') ? 2.5 : 1.8}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {isActive('/technicians') && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-orange-500"></span>
              )}
            </div>
            <span className="text-[10px] font-bold mt-1 tracking-tight">Techs</span>
          </Link>
        )}

        {/* Tab 5: Admin Portal for Admin, Log Repair for Tech, QR Studio otherwise */}
        {isAdmin ? (
          <Link
            to="/admin"
            className={`flex flex-col items-center justify-center py-1 transition-all ${
              isActive('/admin') || isActive('/claims') || isActive('/billing')
                ? 'text-orange-400'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <div className="relative">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={isActive('/admin') || isActive('/claims') || isActive('/billing') ? 2.5 : 1.8}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <circle cx="12" cy="12" r="3" strokeWidth={1.8} />
              </svg>
              {(isActive('/admin') || isActive('/claims') || isActive('/billing')) && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-orange-500"></span>
              )}
            </div>
            <span className="text-[10px] font-bold mt-1 tracking-tight">Admin</span>
          </Link>
        ) : isTechnician ? (
          <Link
            to="/repair/log"
            className={`flex flex-col items-center justify-center py-1 transition-all ${
              isActive('/repair/log') ? 'text-orange-400' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <div className="relative">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={isActive('/repair/log') ? 2.5 : 1.8}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              {isActive('/repair/log') && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-orange-500"></span>
              )}
            </div>
            <span className="text-[10px] font-bold mt-1 tracking-tight">Log Repair</span>
          </Link>
        ) : (
          <Link
            to="/qr-generator"
            className={`flex flex-col items-center justify-center py-1 transition-all ${
              isActive('/qr-generator')
                ? 'text-orange-400'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <div className="relative">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={isActive('/qr-generator') ? 2.5 : 1.8}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {isActive('/qr-generator') && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-orange-500"></span>
              )}
            </div>
            <span className="text-[10px] font-bold mt-1 tracking-tight">QR Studio</span>
          </Link>
        )}
      </div>
    </nav>
  );
};
